import type React from "react";
import { useState } from "react";
import { LockKeyhole, Sparkles } from "lucide-react";
import { useLicense } from "../contexts/LicenseContext";
import { Alert, AlertDescription, AlertTitle, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Switch, toast } from "./ui";

export function ProWall({ children }: { children: React.ReactNode }) {
  const {
    gatingEnabled,
    isProEffective,
    proUnlocked,
    bypassEnabled,
    allowBypass,
    setProUnlocked,
    setBypassEnabled,
    isLoading,
  } = useLicense();
  const [licenseKey, setLicenseKey] = useState("");
  const [isActivating, setIsActivating] = useState(false);

  if (!gatingEnabled || isProEffective) {
    return <>{children}</>;
  }

  const handleActivate = async () => {
    const value = licenseKey.trim();
    if (!value) {
      toast.error("Enter a license key to activate.");
      return;
    }

    setIsActivating(true);
    try {
      // Phase 1 scaffold: any non-empty key unlocks Pro locally.
      await setProUnlocked(true);
      setLicenseKey("");
      toast.success("Pro unlocked on this device.");
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-linear-to-br from-bg-deep to-bg-primary p-6">
      <Card className="w-full max-w-lg border-border-default bg-bg-secondary/95 shadow-2xl backdrop-blur-sm">
        <CardHeader className="space-y-3">
          <div className="flex items-center gap-2">
            <Badge variant="accent" size="sm">Pro</Badge>
            <Badge variant="secondary" size="sm">Desktop</Badge>
          </div>
          <CardTitle className="flex items-center gap-2 text-xl">
            <LockKeyhole className="size-5 text-accent-primary" />
            Pro Access Required
          </CardTitle>
          <CardDescription>
            The desktop app is currently restricted to Pro while feature gating is enabled.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <Sparkles className="size-4 text-accent-primary" />
            <AlertTitle>Activate Pro</AlertTitle>
            <AlertDescription>
              Enter your license key, or use the local switches below for development/testing.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <label htmlFor="license-key" className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
              License Key
            </label>
            <div className="flex gap-2">
              <Input
                id="license-key"
                value={licenseKey}
                onChange={(event) => setLicenseKey(event.target.value)}
                placeholder="XXXX-XXXX-XXXX"
                disabled={isLoading || isActivating}
              />
              <Button
                onClick={handleActivate}
                disabled={isLoading || isActivating || licenseKey.trim().length === 0}
              >
                Activate
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-border-subtle bg-bg-primary/50 p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-text-primary">Pro unlocked</p>
                <p className="text-xs text-text-secondary">
                  Local scaffold switch for Phase 1 testing.
                </p>
              </div>
              <Switch
                checked={proUnlocked}
                onCheckedChange={setProUnlocked}
                disabled={isLoading}
                aria-label="Toggle Pro unlocked"
              />
            </div>

            {allowBypass && (
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-text-primary">Bypass gating (Dev only)</p>
                  <p className="text-xs text-text-secondary">
                    Temporary bypass for local development builds.
                  </p>
                </div>
                <Switch
                  checked={bypassEnabled}
                  onCheckedChange={setBypassEnabled}
                  disabled={isLoading}
                  aria-label="Toggle bypass gating"
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
